import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Search,
  MenuBook,
  BadgeOutlined,
  Print,
  Link,
  CheckCircle,
  WarningAmber,
  ArrowUpward,
  Close,
  ExpandMore,
  ChevronRight,
  InfoOutlined,
  WorkOutline,
  TrendingUp,
  PaymentsOutlined,
  WorkspacePremiumOutlined,
  BusinessCenterOutlined as BusinessCenterOutlinedIcon,
} from "@mui/icons-material";
import FDCEmploymentAcceptance from "./FDCEmploymentAcceptance";
import "./fdcEmploymentTerms.css";

const documentConfig = {
  companyName: "Universe Inventory Management System",
  issuer: "Universe Tech / Universe Inventory Management System",
  documentTitle: "Field Development Consultant (FDC)",
  documentSubtitle:
    "Employment, Performance, Commission & Career Progression Terms",
  position: "Field Development Consultant (FDC)",
  employmentType: "Employee",
  department: "Business Development / Client Acquisition",
  reportingStructure: "As assigned by Universe Inventory Management System",
  version: "1.0",
  status: "Current",
};

const sections = [
  {
    id: "employment-status",
    number: "01",
    title: "Employment Status",
    lines: [
      "Universe Inventory Management System appoints the individual named in the corresponding employment offer as a Field Development Consultant (FDC).",
      "The FDC is engaged as an employee of the company, subject to these Terms, the employee's offer letter, internal company policies, applicable performance requirements, disciplinary procedures, and any additional operational policies issued by Universe Inventory Management System.",
      "The employment relationship is not an independent-contractor arrangement.",
      "The FDC shall perform duties assigned by the company and operate within the geographical territory, state, branch, local government area, market segment, or other operational area allocated by the company.",
    ],
  },
  {
    id: "primary-purpose-of-the-fdc-role",
    number: "02",
    title: "Primary Purpose Of The Fdc Role",
    lines: [
      "The principal responsibility of a Field Development Consultant is to develop the company's customer base by identifying businesses that can benefit from the Universe Inventory Management System, introducing the platform to them, demonstrating its value, assisting them through registration and subscription, and ensuring successful activation.",
      "The FDC therefore serves as one of the principal field acquisition representatives of Universe Inventory.",
      "The role does not end merely at finding prospective businesses.",
      "The FDC is expected to help move qualified prospects through the following process:",
      "Prospecting → Marketing → Consultation → Registration → Subscription Payment → Activation → Initial Training → Adoption",
    ],
  },
  {
    id: "fdc-duties-and-responsibilities",
    number: "03",
    title: "Fdc Duties And Responsibilities",
    lines: [
      "The duties of a Field Development Consultant include, but are not limited to:",
    ],
    subpoints: [
      {
        id: "identify-businesses-and-organizations-that-may-require-inventory-management-sales-management-point-of-sale-business-management-or-other-solutions-provided-through-universe-inventory",
        number: "01",
        title:
          "Identify businesses and organizations that may require inventory management, sales management, point-of-sale, business-management, or other solutions provided through Universe Inventory.",
      },
      {
        id: "approach-prospective-clients-professionally-and-introduce-universe-inventory-products-and-services",
        number: "02",
        title:
          "Approach prospective clients professionally and introduce Universe Inventory products and services.",
      },
      {
        id: "explain-the-capabilities-benefits-subscription-plans-and-relevant-features-of-universe-inventory-to-prospective-clients",
        number: "03",
        title:
          "Explain the capabilities, benefits, subscription plans, and relevant features of Universe Inventory to prospective clients.",
      },
      {
        id: "conduct-field-marketing-and-direct-client-acquisition-activities-within-assigned-operational-territories",
        number: "04",
        title:
          "Conduct field marketing and direct client acquisition activities within assigned operational territories.",
      },
      {
        id: "assist-prospective-clients-with-the-registration-process",
        number: "05",
        title: "Assist prospective clients with the registration process.",
      },
      {
        id: "assist-clients-in-selecting-an-appropriate-universe-inventory-subscription-plan",
        number: "06",
        title:
          "Assist clients in selecting an appropriate Universe Inventory subscription plan.",
      },
      {
        id: "follow-up-with-registered-clients-until-the-required-subscription-payment-has-been-completed",
        number: "07",
        title:
          "Follow up with registered clients until the required subscription payment has been completed.",
      },
      {
        id: "assist-with-the-initial-activation-of-the-client-s-universe-inventory-account",
        number: "08",
        title:
          "Assist with the initial activation of the client's Universe Inventory account.",
      },
      {
        id: "provide-initial-guidance-and-training-to-newly-activated-clients-on-how-to-use-the-software",
        number: "09",
        title:
          "Provide initial guidance and training to newly activated clients on how to use the software.",
      },
      {
        id: "help-clients-understand-essential-functions-of-the-system-during-their-onboarding-period",
        number: "10",
        title:
          "Help clients understand essential functions of the system during their onboarding period.",
      },
      {
        id: "maintain-professional-relationships-with-clients-acquired-through-the-fdc-s-activities",
        number: "11",
        title:
          "Maintain professional relationships with clients acquired through the FDC's activities.",
      },
      {
        id: "maintain-accurate-records-of-prospects-registrations-activations-subscriptions-and-client-follow-ups",
        number: "12",
        title:
          "Maintain accurate records of prospects, registrations, activations, subscriptions, and client follow-ups.",
      },
      {
        id: "submit-activity-and-performance-information-through-the-company-s-designated-systems-and-dashboards",
        number: "13",
        title:
          "Submit activity and performance information through the company's designated systems and dashboards.",
      },
      {
        id: "work-toward-and-meet-assigned-monthly-client-acquisition-targets",
        number: "14",
        title:
          "Work toward and meet assigned monthly client-acquisition targets.",
      },
      {
        id: "cooperate-with-support-technical-customer-success-sales-and-management-teams-where-a-client-requires-assistance-beyond-the-responsibilities-of-the-fdc",
        number: "15",
        title:
          "Cooperate with support, technical, customer-success, sales, and management teams where a client requires assistance beyond the responsibilities of the FDC.",
      },
      {
        id: "protect-the-reputation-brand-confidential-information-software-intellectual-property-and-commercial-interests-of-universe-inventory-management-system",
        number: "16",
        title:
          "Protect the reputation, brand, confidential information, software, intellectual property, and commercial interests of Universe Inventory Management System.",
      },
    ],
  },
  {
    id: "client-support-after-activation",
    number: "04",
    title: "Client Support After Activation",
    lines: [
      "The Field Development Consultant is not the primary long-term customer-support officer for clients.",
      "Universe Inventory shall maintain appropriate support and management functions responsible for routine client support.",
      "However, an FDC retains a relationship responsibility toward clients originally acquired by the FDC.",
      "Where an acquired client becomes inactive, stops using the platform, fails to renew a subscription, allows an account to become suspended, or otherwise disengages from Universe Inventory, the FDC may be required to contact or visit the client.",
      "The purpose of such follow-up is to:",
      "•\tdetermine why the client became inactive;",
      "•\tidentify any difficulty preventing continued use;",
      "•\tprovide appropriate guidance;",
      "•\treconnect the client with the relevant support department;",
      "•\tassist with subscription renewal where appropriate;",
      "•\thelp resolve adoption difficulties; and",
      "•\tencourage the client to return to active use of Universe Inventory.",
      "The FDC shall not be expected to personally resolve technical matters that properly belong to the company's technical or customer-support departments.",
    ],
  },
  {
    id: "definition-of-a-qualified-client",
    number: "05",
    title: "Definition Of A Verified Active Client (VAC)",
    lines: [
      "A Verified Active Client (VAC) is a genuinely acquired customer whose business is registered with Universe Inventory, whose service account is activated, and whose qualifying subscription payments are valid and verified.",
      "Monthly subscription pathway: the customer must complete two successfully verified monthly subscription payments; the second must be made within 45 days of the first successful payment.",
      "Annual subscription pathway: the customer must complete one successfully verified annual subscription payment. A second payment is not required for an annual subscriber.",
      "The customer must meet the applicable registration, activation, verified payment and valid FDC-attribution conditions before generating VAC credit for performance, commission, salary eligibility or progression.",
    ],
    afterSubpoints: [
      "Registration, activation or a free trial alone does not qualify a customer as a VAC. One monthly payment alone does not qualify.",
      "A second monthly payment outside the 45-day qualification period does not meet the initial monthly VAC rule.",
      "Duplicate, fraudulent, refunded or invalid transactions shall not generate VAC credit; each genuine acquisition must be counted only once for a given milestone.",
    ],
  },
  {
    id: "monthly-client-acquisition-target",
    number: "06",
    title: "Monthly Client Acquisition Target",
    lines: [
      "The new FDC starts at Level 1 with a target of 20 new Clients per month. Targets increase as the employee advances through the FDC levels.",
      "Level 1: 20 new Clients per month; Level 2: 40; Level 3: 100; Level 4: 200; Level 5: 300; Level 6: 400; Level 7: 1,000.",
      "Monthly performance is measured against the target of the employee’s current level. For example, a Level 1 FDC acquiring 20, 30 or 40 Clients achieves 100%, 150% or 200% of their Level 1 monthly target respectively.",
      "Over-performance contributes toward the applicable cumulative promotion milestone, even when that milestone is reached sooner than six months.",
    ],
  },
  {
    id: "performance-linked-salary",
    number: "07",
    title: "Performance-Linked Salary",
    lines: [
      "The FDC salary structure is performance-linked, subject to the initial cumulative salary eligibility milestone of fifteen (15) Verified Active Clients (VACs).",
      "An FDC enters the existing monthly performance-linked salary framework upon acquiring a cumulative total of 15 valid VACs. This is a one-time entry milestone, not an additional monthly acquisition requirement.",
      "Before the 15-VAC milestone, the FDC may receive approved commissions on verified acquisitions and other compensation consistent with the employment agreement and applicable law; the regular performance-linked salary schedule is not an automatic entitlement under this policy before eligibility is reached.",
      "Once salary eligibility has been achieved, the FDC remains in the existing salary framework and monthly base salary is calculated using the approved performance schedule applicable to the employee’s level.",
      "The Level 1 monthly acquisition target is 20 new Clients. The existing Level 1 salary-performance bands and amounts below remain unchanged.",
      "At Level 1, full-target monthly base salary is ₦100,000, applicable from 70% performance (14 Clients out of 20), once the initial salary eligibility milestone has been fulfilled.",
      "The Level 1 salary table includes a ₦15,000 minimum band for 0%–10% monthly achievement, applicable after salary eligibility has been reached and subject to the employment agreement and applicable law.",
    ],
  },
  {
    id: "client-acquisition-commission",
    number: "08",
    title: "Client Acquisition Commission",
    lines: [
      "An FDC may earn a client-acquisition commission for each attributed Verified Active Client (VAC) whose qualification requirements have been fulfilled under Section 05.",
      "The rate depends on the customer’s purchased subscription plan:",
      "• Enterprise — ₦15,000 per VAC.",
      "• Premium — ₦8,000 per VAC.",
      "• Professional — ₦5,000 per VAC.",
      "• Standard — ₦3,000 per VAC.",
      "For monthly subscribers, qualification requires two valid monthly payments within 45 days of the first. For annual subscribers, one verified annual payment is sufficient, provided registration and account activation requirements are met.",
      "Commissions are separate from monthly base salary and may accrue before the one-time 15-VAC salary entry milestone, subject to the applicable compensation terms.",
      "The plan-specific commission schedules below form part of this compensation framework; their displayed base salaries and total earnings assume that the employee has already fulfilled the 15-VAC salary eligibility milestone.",
    ],
  },
  {
    id: "commission-across-fdc-levels",
    number: "09",
    title: "Commission Across Fdc Levels",
    lines: [
      "The applicable acquisition commission continues to be determined by the subscription plan purchased by the Verified Active Client (VAC).",
      "Promotion from Level 1 to Level 2, Level 3, or another FDC rank does not automatically change the commission rate attached to a Standard, Professional, Premium, or Enterprise client.",
      "Higher FDC ranks receive increased salary potential primarily through the rank-based compensation structure unless the company formally revises the applicable commission schedule.",
    ],
  },
  {
    id: "fdc-career-levels",
    number: "10",
    title: "Fdc Career Levels",
    lines: [
      "The Field Development Consultant career structure contains seven levels. Full-target monthly salary and the current-level monthly acquisition target are as follows:",
      "Each promotion milestone uses newly acquired VACs credited toward that particular level. Six months at the listed monthly target is only an illustration; no six-month waiting period is required once the relevant cumulative milestone is achieved and verified.",
      "Level 7 is the highest FDC level. An employee at Level 7 may be considered for a separate leadership or executive role, including State Director, Regional Manager or Executive Director, at management’s discretion; such appointment is not guaranteed.",
    ],
  },
  {
    id: "rank-progression",
    number: "11",
    title: "Rank Progression",
    lines: [
      "FDC promotion is determined primarily by verified, cumulative new VAC acquisitions credited toward the next level, rather than a fixed number of months or an average-percentage benchmark.",
      "The promotion milestones are level-specific: Level 1 → 2 requires 120 new VACs; Level 2 → 3 requires 240; Level 3 → 4 requires 600; Level 4 → 5 requires 1,200; Level 5 → 6 requires 1,800; and Level 6 → 7 requires 2,400.",
      "Illustrative six-month progression at the current-level monthly targets:",
      "These six-month examples illustrate the arithmetic only. An FDC can meet the applicable milestone in less than six months or take longer. There is no mandatory six-month waiting period.",
      "The employee becomes eligible for promotion review when the applicable new-VAC milestone is achieved; promotion remains subject to management verification and approval.",
    ],
  },
  {
    id: "over-performance-and-accelerated-progression",
    number: "12",
    title: "Over-Performance And Accelerated Progression",
    lines: [
      "Every valid newly acquired VAC counts toward the cumulative milestone for the employee’s current FDC level. Exceptional performance can therefore accelerate progression.",
      "For example, a Level 1 FDC acquiring 40 new VACs monthly can reach the required 120 VACs in three months rather than six. An employee who acquires 120 verified VACs even sooner may qualify for promotion review sooner.",
      "At Level 2, the next promotion requires a separate 240 new VACs; at Level 3, a separate 600; at Level 4, 1,200; at Level 5, 1,800; and at Level 6, 2,400.",
      "Previously credited VACs from a completed level milestone must not be reused for a later promotion. Lifetime acquisition history must be preserved separately from credits toward the current promotion milestone.",
      "Meeting a numerical threshold establishes eligibility for management review, not automatic promotion. Client legitimacy, attribution, records, and applicable professional conduct requirements must be verified.",
      "Level 7 is the terminal FDC career level. Future consideration for higher organizational leadership is discretionary and does not constitute an automatic eighth FDC promotion.",
    ],
  },
  {
    id: "new-fdc-initial-performance-period",
    number: "13",
    title: "New Fdc Initial Performance Period",
    lines: [
      "A newly appointed FDC shall be expected to establish an acceptable level of client acquisition during the first three months.",
      "A new FDC who fails to acquire at least:",
      "20 Clients Within the First Three Months",
      "may be placed under Performance Suspension and Recovery.",
      "The purpose of this process is corrective rather than automatically terminating the employee.",
    ],
  },
  {
    id: "performance-suspension",
    number: "14",
    title: "Performance Suspension",
    lines: [
      "Performance suspension is a temporary restriction from active FDC duties for a period expressly determined and communicated by management. It is not, by itself, dismissal, employment termination or account deletion.",
      "During suspension, an FDC may continue signing in and viewing their existing dashboard, historical performance, earnings, commissions, employment notices and reinstatement instructions, subject to appropriate read-only access.",
      "During suspension, the employee must not undertake active official marketing, onboard new clients or generate new client acquisition attribution through the FDC account.",
      "The employee’s existing referral code and historical records must be preserved, but the code must be disabled for new registrations and attribution during suspension.",
      "Suspension may last a management-approved period, including two or three months as examples, but no fixed duration is imposed by this clause.",
      "Upon formal reinstatement, approved marketing, onboarding and referral privileges may be restored. An authorized recovery period must be distinguished from an operational suspension.",
    ],
  },
  {
    id: "performance-recovery-programme",
    number: "15",
    title: "Performance Recovery Programme",
    lines: [
      "The recovery programme may operate for approximately:",
      "One Month",
      "During this period, the company may assign intensified weekly acquisition targets.",
      "Depending on the employee's circumstances and the company's decision, such a target may include, for example:",
      "•\t10 verified active clients (VACs) per week;",
      "•\t20 verified active clients (VACs) per week; or",
      "•\tanother recovery target formally assigned by management.",
      "The precise target shall be communicated to the affected FDC.",
      "The purpose of the recovery programme is to determine whether the employee is prepared and able to rebuild their performance and return to normal FDC status.",
      "A suspended FDC cannot acquire new clients while operational privileges remain disabled. Where management explicitly authorizes acquisition duties as part of recovery, the recovery permission must be separately communicated and does not automatically restore all suspended privileges.",
    ],
  },
  {
    id: "compensation-during-performance-recovery",
    number: "16",
    title: "Compensation During Performance Recovery",
    lines: [
      "An employee placed under the performance recovery programme shall not automatically lose all compensation.",
      "The employee may continue to receive:",
    ],
    subpoints: [
      {
        id: "a-substantially-reduced-performance-salary-and",
        number: "01",
        title: "A substantially reduced performance salary; and",
      },
      {
        id: "applicable-3-000-commissions-on-qualified-clients-successfully-acquired",
        number: "02",
        title:
          "Applicable client-acquisition commissions on Verified Active Clients at the plan-based rates in Section 08.",
      },
    ],
    afterSubpoints: [
      "The recovery salary shall be significantly lower than the employee's normal rank-based full-target salary.",
      "The exact recovery percentage may be established in the company's compensation schedule.",
      "Performance recovery does not create a separate flat-rate commission. Standard, Professional, Premium and Enterprise VACs retain the applicable Section 08 plan-based rates.",
    ],
  },
  {
    id: "failure-of-the-recovery-programme",
    number: "17",
    title: "Failure Of The Recovery Programme",
    lines: [
      "Where an FDC fails to satisfy the assigned recovery requirements, the company may determine the appropriate next action.",
      "Depending upon the circumstances, this may include:",
      "•\textending the recovery programme;",
      "•\tmaintaining the employee under suspension;",
      "•\tassigning additional retraining;",
      "•\trestructuring the employee's duties;",
      "•\tpermanent suspension;",
      "•\tdisciplinary action; or",
      "•\tother employment action permitted under company policy.",
      "The final decision shall be made by Universe Inventory Management System after reviewing the circumstances of the employee.",
      "Continued suspension does not automatically delete the employee’s account or historical records. Any recovery activity requires appropriate management authorization and operational privileges.",
    ],
  },
  {
    id: "100-client-career-protection-milestone",
    number: "18",
    title: "100-VAC Career Protection Milestone",
    lines: [
      "An important long-term performance milestone shall apply where an FDC has successfully built a portfolio of:",
      "100 Verified Active Clients (VACs)",
      "After reaching 100 cumulative verified active clients (VACs), the FDC shall receive enhanced employment protection against ordinary performance-based termination.",
      "Reaching this milestone demonstrates that the FDC has made a substantial contribution toward building the Universe Inventory customer base.",
      "An FDC who has reached this milestone shall not ordinarily be removed from the company merely because their acquisition performance subsequently falls below the normal monthly target.",
      "Instead, long-term inactivity shall be handled through the retirement framework described below.",
      "This protection relates principally to performance-based separation and does not remove the employee's obligation to comply with company rules, lawful instructions, confidentiality requirements, anti-fraud rules, or other disciplinary standards.",
    ],
  },
  {
    id: "six-month-inactivity-rule",
    number: "19",
    title: "Six-Month Inactivity Rule",
    lines: [
      "Where an FDC who has already reached the 100-client milestone remains substantially inactive and fails to perform meaningful FDC activities for a continuous period of approximately:",
      "Six Months",
      "the company may formally contact the employee to determine whether the employee intends and is able to continue working.",
      "The employee may be offered an opportunity to explain the inactivity and indicate whether they wish to return to active service.",
    ],
  },
  {
    id: "retirement-after-extended-inactivity",
    number: "20",
    title: "Retirement After Extended Inactivity",
    lines: [
      "Where the employee has achieved the 100-client milestone but, after approximately six months of inactivity, confirms that they cannot or do not wish to continue performing the role, the company may commence a retirement process rather than treating the matter as an ordinary performance termination.",
      "The company may then:",
    ],
    subpoints: [
      {
        id: "formally-retire-the-employee-from-active-fdc-service",
        number: "01",
        title: "formally retire the employee from active FDC service;",
      },
      {
        id: "calculate-any-applicable-retirement-entitlement",
        number: "02",
        title: "calculate any applicable retirement entitlement;",
      },
      {
        id: "settle-approved-benefits-owed-to-the-employee",
        number: "03",
        title: "settle approved benefits owed to the employee;",
      },
      {
        id: "complete-handover-procedures",
        number: "04",
        title: "complete handover procedures;",
      },
      {
        id: "close-or-transition-the-employee-s-operational-responsibilities-and",
        number: "05",
        title:
          "close or transition the employee's operational responsibilities; and",
      },
      {
        id: "formally-release-the-employee-from-active-service",
        number: "06",
        title: "formally release the employee from active service.",
      },
    ],
  },
  {
    id: "retirement-benefits",
    number: "21",
    title: "Retirement Benefits",
    lines: [
      "An eligible retiring FDC may receive retirement benefits in accordance with the company's approved retirement-benefit structure applicable at the time of retirement.",
      "The specific retirement-benefit calculation shall be issued separately once the company's retirement formula has been finalized.",
      "Reaching the 100-client milestone does not itself immediately trigger retirement benefits.",
      "The employee must satisfy the company's applicable retirement conditions and complete the required exit process.",
    ],
  },
  {
    id: "client-ownership",
    number: "22",
    title: "Client Ownership",
    lines: [
      "Clients acquired by an FDC remain customers of Universe Inventory Management System.",
      "An FDC does not personally own the customer account or obtain an independent proprietary right over the client.",
      "Client assignment to the employee is primarily for:",
      "•\tattribution;",
      "•\tcommission calculation;",
      "•\tperformance measurement;",
      "•\tretention responsibility;",
      "•\trelationship tracking; and",
      "•\tcareer progression.",
      "Universe Inventory retains authority over all customer accounts, subscriptions, pricing, access, support arrangements, and client assignments.",
    ],
  },
  {
    id: "client-record-integrity",
    number: "23",
    title: "Client Record Integrity",
    lines: [
      "An FDC must not:",
      "•\tcreate fictitious clients;",
      "•\tcreate duplicate client accounts for the purpose of increasing commission;",
      "•\tregister false businesses;",
      "•\tmanipulate subscription records;",
      "•\tmake false claims regarding payment;",
      "•\tfalsely record activation;",
      "•\tclaim another FDC's client;",
      "•\tmanipulate referral information; or",
      "•\tengage in any activity intended to artificially increase salary, commission, target achievement, or promotion metrics.",
      "Commission and performance credit may be reversed where a transaction is fraudulent, duplicated, invalid, cancelled under applicable company rules, or incorrectly attributed.",
    ],
  },
  {
    id: "professional-conduct",
    number: "24",
    title: "Professional Conduct",
    lines: [
      "An FDC represents Universe Inventory before prospective and existing customers.",
      "Employees are therefore expected to:",
      "•\tact professionally;",
      "•\tprovide accurate information;",
      "•\tavoid misleading marketing claims;",
      "•\trespect client privacy;",
      "•\tcomply with company instructions;",
      "•\tavoid abusive or threatening conduct;",
      "•\tprotect company property;",
      "•\tsafeguard account credentials;",
      "•\tmaintain proper records; and",
      "•\tpreserve the reputation of Universe Inventory.",
    ],
  },
  {
    id: "confidentiality",
    number: "25",
    title: "Confidentiality",
    lines: [
      "During and after employment, an FDC shall protect confidential information belonging to Universe Inventory.",
      "Confidential information may include:",
      "•\tcustomer information;",
      "•\tpricing strategies;",
      "•\tinternal compensation structures;",
      "•\tcompany databases;",
      "•\tclient leads;",
      "•\temployee information;",
      "•\tinternal operational procedures;",
      "•\tunreleased features;",
      "•\tsource code;",
      "•\ttechnical infrastructure;",
      "•\taccess credentials;",
      "•\tcommercial strategies;",
      "•\tfinancial information;",
      "•\tmarketing plans;",
      "•\tinternal documents; and",
      "•\tother non-public company information.",
      "Confidential information shall not be disclosed to unauthorized persons unless required by law or expressly authorized by the company.",
    ],
  },
  {
    id: "company-systems-and-performance-dashboard",
    number: "26",
    title: "Company Systems And Performance Dashboard",
    lines: [
      "Universe Inventory may maintain an FDC digital workspace or dashboard showing information such as:",
      "•\tmonthly target;",
      "•\tpercentage achieved;",
      "•\tacquired clients;",
      "•\tverified active clients (VACs);",
      "•\tcommission;",
      "•\tsalary status;",
      "•\tcurrent FDC level;",
      "•\tprogression status;",
      "•\tsix-month performance;",
      "•\tclient activity;",
      "•\tinactive clients;",
      "•\trecovery status;",
      "•\twarnings;",
      "•\ttraining;",
      "•\trank;",
      "•\tretirement eligibility;",
      "•\temployment status; and",
      "•\tother FDC performance information.",
      "Company system records shall form an important part of performance and compensation verification.",
    ],
  },
  {
    id: "salary-and-commission-verification",
    number: "27",
    title: "Salary And Commission Verification",
    lines: [
      "All salary and commission calculations shall be subject to verification of:",
      "•\tclient identity;",
      "•\tregistration;",
      "•\tpayment;",
      "•\tsubscription validity;",
      "•\tactivation;",
      "•\tFDC attribution; and",
      "•\tabsence of fraud or duplication.",
      "Where a discrepancy is found, the company may temporarily withhold the disputed component while conducting verification.",
      "Valid undisputed compensation should continue to be processed according to company payroll procedures.",
    ],
  },
  {
    id: "policy-changes",
    number: "28",
    title: "Policy Changes",
    lines: [
      "As Universe Inventory grows, the company may revise operational targets, compensation structures, ranking requirements, commissions, recovery procedures, and other employment policies.",
      "Material changes affecting employees should be formally communicated through an appropriate company channel.",
      "Changes should ordinarily operate prospectively rather than altering compensation already validly earned before the effective date of the change.",
    ],
  },
  {
    id: "company-discretion",
    number: "29",
    title: "Company Discretion",
    lines: [
      "Where circumstances arise that are not specifically addressed in these Terms, Universe Inventory Management System may make an appropriate decision after considering:",
      "•\temployee performance;",
      "•\temployment history;",
      "•\tfairness;",
      "•\tcompany policy;",
      "•\tclient impact;",
      "•\tmisconduct, if any;",
      "•\tprevious warnings;",
      "•\toperational requirements; and",
      "•\tother relevant circumstances.",
      "Such discretion should be exercised consistently with the employee's employment agreement and applicable company procedures.",
    ],
  },
  {
    id: "acceptance",
    number: "30",
    title: "Acceptance",
    lines: [
      "By accepting appointment as a Field Development Consultant, the employee confirms that they:",
      "•\thave read and understood these Terms and the VAC qualification pathways in Section 05 (two verified monthly payments within 45 days, or one verified annual payment);",
      "•\tunderstand the one-time 15-VAC salary eligibility milestone and the existing performance-linked salary framework;",
      "•\tunderstand the monthly acquisition targets and level-specific promotion milestones described in Sections 06 and 10–12;",
      "•\tunderstand that six months is only an illustration and accelerated promotion review is possible when the applicable cumulative VAC threshold is reached;",
      "•\tunderstand the plan-based commissions in Section 08, including their application during authorized performance recovery;",
      "•\tunderstand temporary suspension, restricted onboarding and referral use, and authorized reinstatement;",
      "•\tunderstand the 100-VAC career-protection milestone, six-month inactivity and retirement framework; and",
      "•\tagree to comply with Universe Inventory Management System policies and applicable employment requirements.",
    ],
  },
];

const salaryBands = {
  headers: ["Monthly Performance", "Base Salary"],
  rows: [
    ["0%–10%", "₦15,000"],
    ["15%–19%", "₦20,000"],
    ["20%–29%", "₦25,000"],
    ["30%–39%", "₦40,000"],
    ["40%–49%", "₦55,000"],
    ["50%–59%", "₦70,000"],
    ["60%–69%", "₦85,000"],
    ["70%–100%", "₦100,000"],
  ],
};

const commissionSchedules = {
  15000: {
    title: "₦15,000 Commission Per Client",
    rate: "₦15,000",
    headers: [
      "Achievement",
      "Clients",
      "Base Salary",
      "Commission",
      "Total Monthly Earnings",
    ],
    rows: [
      ["0%", "0", "₦15,000", "₦0", "₦15,000"],
      ["5%", "1", "₦15,000", "₦15,000", "₦30,000"],
      ["10%", "2", "₦15,000", "₦30,000", "₦45,000"],
      ["15%", "3", "₦20,000", "₦45,000", "₦65,000"],
      ["20%", "4", "₦25,000", "₦60,000", "₦85,000"],
      ["25%", "5", "₦25,000", "₦75,000", "₦100,000"],
      ["30%", "6", "₦40,000", "₦90,000", "₦130,000"],
      ["35%", "7", "₦40,000", "₦105,000", "₦145,000"],
      ["40%", "8", "₦55,000", "₦120,000", "₦175,000"],
      ["45%", "9", "₦55,000", "₦135,000", "₦190,000"],
      ["50%", "10", "₦70,000", "₦150,000", "₦220,000"],
      ["55%", "11", "₦70,000", "₦165,000", "₦235,000"],
      ["60%", "12", "₦85,000", "₦180,000", "₦265,000"],
      ["65%", "13", "₦85,000", "₦195,000", "₦280,000"],
      ["70%", "14", "₦100,000", "₦210,000", "₦310,000"],
      ["75%", "15", "₦100,000", "₦225,000", "₦325,000"],
      ["80%", "16", "₦100,000", "₦240,000", "₦340,000"],
      ["85%", "17", "₦100,000", "₦255,000", "₦355,000"],
      ["90%", "18", "₦100,000", "₦270,000", "₦370,000"],
      ["95%", "19", "₦100,000", "₦285,000", "₦385,000"],
      ["100%", "20", "₦100,000", "₦300,000", "₦400,000"],
    ],
    maximum: "₦400,000",
  },
  8000: {
    title: "₦8,000 Commission Per Client",
    rate: "₦8,000",
    headers: [
      "Achievement",
      "Clients",
      "Base Salary",
      "Commission",
      "Total Monthly Earnings",
    ],
    rows: [
      ["0%", "0", "₦15,000", "₦0", "₦15,000"],
      ["5%", "1", "₦15,000", "₦8,000", "₦23,000"],
      ["10%", "2", "₦15,000", "₦16,000", "₦31,000"],
      ["15%", "3", "₦20,000", "₦24,000", "₦44,000"],
      ["20%", "4", "₦25,000", "₦32,000", "₦57,000"],
      ["25%", "5", "₦25,000", "₦40,000", "₦65,000"],
      ["30%", "6", "₦40,000", "₦48,000", "₦88,000"],
      ["35%", "7", "₦40,000", "₦56,000", "₦96,000"],
      ["40%", "8", "₦55,000", "₦64,000", "₦119,000"],
      ["45%", "9", "₦55,000", "₦72,000", "₦127,000"],
      ["50%", "10", "₦70,000", "₦80,000", "₦150,000"],
      ["55%", "11", "₦70,000", "₦88,000", "₦158,000"],
      ["60%", "12", "₦85,000", "₦96,000", "₦181,000"],
      ["65%", "13", "₦85,000", "₦104,000", "₦189,000"],
      ["70%", "14", "₦100,000", "₦112,000", "₦212,000"],
      ["75%", "15", "₦100,000", "₦120,000", "₦220,000"],
      ["80%", "16", "₦100,000", "₦128,000", "₦228,000"],
      ["85%", "17", "₦100,000", "₦136,000", "₦236,000"],
      ["90%", "18", "₦100,000", "₦144,000", "₦244,000"],
      ["95%", "19", "₦100,000", "₦152,000", "₦252,000"],
      ["100%", "20", "₦100,000", "₦160,000", "₦260,000"],
    ],
    maximum: "₦260,000",
  },
  5000: {
    title: "₦5,000 Commission Per Client",
    rate: "₦5,000",
    headers: [
      "Achievement",
      "Clients",
      "Base Salary",
      "Commission",
      "Total Monthly Earnings",
    ],
    rows: [
      ["0%", "0", "₦15,000", "₦0", "₦15,000"],
      ["5%", "1", "₦15,000", "₦5,000", "₦20,000"],
      ["10%", "2", "₦15,000", "₦10,000", "₦25,000"],
      ["15%", "3", "₦20,000", "₦15,000", "₦35,000"],
      ["20%", "4", "₦25,000", "₦20,000", "₦45,000"],
      ["25%", "5", "₦25,000", "₦25,000", "₦50,000"],
      ["30%", "6", "₦40,000", "₦30,000", "₦70,000"],
      ["35%", "7", "₦40,000", "₦35,000", "₦75,000"],
      ["40%", "8", "₦55,000", "₦40,000", "₦95,000"],
      ["45%", "9", "₦55,000", "₦45,000", "₦100,000"],
      ["50%", "10", "₦70,000", "₦50,000", "₦120,000"],
      ["55%", "11", "₦70,000", "₦55,000", "₦125,000"],
      ["60%", "12", "₦85,000", "₦60,000", "₦145,000"],
      ["65%", "13", "₦85,000", "₦65,000", "₦150,000"],
      ["70%", "14", "₦100,000", "₦70,000", "₦170,000"],
      ["75%", "15", "₦100,000", "₦75,000", "₦175,000"],
      ["80%", "16", "₦100,000", "₦80,000", "₦180,000"],
      ["85%", "17", "₦100,000", "₦85,000", "₦185,000"],
      ["90%", "18", "₦100,000", "₦90,000", "₦190,000"],
      ["95%", "19", "₦100,000", "₦95,000", "₦195,000"],
      ["100%", "20", "₦100,000", "₦100,000", "₦200,000"],
    ],
    maximum: "₦200,000",
  },
  3000: {
    title: "₦3,000 Commission Per Client",
    rate: "₦3,000",
    headers: [
      "Achievement",
      "Clients",
      "Base Salary",
      "Commission",
      "Total Monthly Earnings",
    ],
    rows: [
      ["0%", "0", "₦15,000", "₦0", "₦15,000"],
      ["5%", "1", "₦15,000", "₦3,000", "₦18,000"],
      ["10%", "2", "₦15,000", "₦6,000", "₦21,000"],
      ["15%", "3", "₦20,000", "₦9,000", "₦29,000"],
      ["20%", "4", "₦25,000", "₦12,000", "₦37,000"],
      ["25%", "5", "₦25,000", "₦15,000", "₦40,000"],
      ["30%", "6", "₦40,000", "₦18,000", "₦58,000"],
      ["35%", "7", "₦40,000", "₦21,000", "₦61,000"],
      ["40%", "8", "₦55,000", "₦24,000", "₦79,000"],
      ["45%", "9", "₦55,000", "₦27,000", "₦82,000"],
      ["50%", "10", "₦70,000", "₦30,000", "₦100,000"],
      ["55%", "11", "₦70,000", "₦33,000", "₦103,000"],
      ["60%", "12", "₦85,000", "₦36,000", "₦121,000"],
      ["65%", "13", "₦85,000", "₦39,000", "₦124,000"],
      ["70%", "14", "₦100,000", "₦42,000", "₦142,000"],
      ["75%", "15", "₦100,000", "₦45,000", "₦145,000"],
      ["80%", "16", "₦100,000", "₦48,000", "₦148,000"],
      ["85%", "17", "₦100,000", "₦51,000", "₦151,000"],
      ["90%", "18", "₦100,000", "₦54,000", "₦154,000"],
      ["95%", "19", "₦100,000", "₦57,000", "₦157,000"],
      ["100%", "20", "₦100,000", "₦60,000", "₦160,000"],
    ],
    maximum: "₦160,000",
  },
};

const commissionPlanMeta = {
  15000: {
    table: "Table 1",
    plan: "Enterprise",
    planPrice: "₦31,850/mo",
    clientLabel: "Enterprise plan clients",
    accent: "enterprise",
  },
  8000: {
    table: "Table 2",
    plan: "Premium",
    planPrice: "₦16,500/mo",
    clientLabel: "Premium plan clients",
    accent: "premium",
  },
  5000: {
    table: "Table 3",
    plan: "Professional",
    planPrice: "₦7,500/mo",
    clientLabel: "Professional plan clients",
    accent: "professional",
  },
  3000: {
    table: "Table 4",
    plan: "Standard",
    planPrice: "₦4,850/mo",
    clientLabel: "Standard plan clients",
    accent: "standard",
  },
};

const normalizeSearchText = (section) =>
  [
    section.title,
    ...(section.lines || []),
    ...(section.subpoints || []).map((point) => point.title),
    ...(section.afterSubpoints || []),
  ]
    .join(" ")
    .toLowerCase();

function DataTable({ title, subtitle, headers, rows, compact = false }) {
  return (
    <div
      className={`fdc-terms-table-card ${compact ? "fdc-terms-table-card--compact" : ""}`}
    >
      {title && (
        <div className="fdc-terms-table-card__header">
          <div>
            <strong>{title}</strong>
            {subtitle && <span>{subtitle}</span>}
          </div>
        </div>
      )}

      <div
        className="fdc-terms-table-wrap"
        role="region"
        aria-label={title || "Employment compensation table"}
        tabIndex="0"
      >
        <table className="fdc-terms-table">
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${title || "table"}-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CommissionPlanTable({ scheduleKey }) {
  const schedule = commissionSchedules[scheduleKey];
  const meta = commissionPlanMeta[scheduleKey];

  return (
    <section className={`fdc-comp-plan fdc-comp-plan--${meta.accent}`}>
      <header className="fdc-comp-plan__header">
        <div className="fdc-comp-plan__identity">
          <div className="fdc-comp-plan__table-label">{meta.table}</div>

          <div className="fdc-comp-plan__plan">
            <div className="fdc-comp-plan__icon">
              <BusinessCenterOutlinedIcon />
            </div>

            <div>
              <span className="fdc-comp-plan__eyebrow">
                UNIVERSE INVENTORY PLAN
              </span>
              <h4>{meta.plan}</h4>
              <p>{meta.clientLabel}</p>
            </div>
          </div>
        </div>

        <div className="fdc-comp-plan__metrics">
          <div>
            <span>Plan price</span>
            <strong>{meta.planPrice}</strong>
          </div>

          <div>
            <span>Commission / client</span>
            <strong>{schedule.rate}</strong>
          </div>

          <div>
            <span>Monthly target</span>
            <strong>20 clients</strong>
          </div>

          <div className="fdc-comp-plan__metric-highlight">
            <span>Maximum at 100%</span>
            <strong>{schedule.maximum}</strong>
          </div>
        </div>
      </header>

      <div
        className="fdc-comp-plan__scroller"
        role="region"
        aria-label={`${meta.plan} commission schedule`}
        tabIndex="0"
      >
        <div className="fdc-comp-plan__grid fdc-comp-plan__grid--header">
          <div>Achievement</div>
          <div>Verified Active Clients (VACs)</div>
          <div>Plan Commission</div>
          <div>Total Monthly Earnings</div>
        </div>

        {schedule.rows.map((row, rowIndex) => (
          <div
            className={`fdc-comp-plan__grid fdc-comp-plan__row ${
              rowIndex === schedule.rows.length - 1
                ? "fdc-comp-plan__row--target"
                : ""
            }`}
            key={`${scheduleKey}-${rowIndex}`}
          >
            <div className="fdc-comp-plan__achievement">
              <span>{row[0]}</span>
              <div className="fdc-comp-plan__progress">
                <i style={{ width: row[0] }} />
              </div>
            </div>

            <div>
              <strong>{row[1]}</strong>
            </div>

            <div className="fdc-comp-plan__commission">{row[3]}</div>
            <div className="fdc-comp-plan__total">{row[4]}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FDCEmploymentAcceptanceModal({ onClose }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fdc-acceptance-modal-portal"
      role="dialog"
      aria-modal="true"
      aria-label="FDC Employee Acceptance Form"
    >
      <div
        className="fdc-acceptance-modal__backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="fdc-acceptance-modal__panel">
          <button
            type="button"
            className="fdc-acceptance-modal__close"
            onClick={onClose}
            aria-label="Close employee acceptance form"
          >
            <Close aria-hidden="true" />
          </button>

          <FDCEmploymentAcceptance embedded onClose={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FDCEmploymentTerms() {
  const [searchValue, setSearchValue] = useState("");
  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [copiedSection, setCopiedSection] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const sectionRefs = useRef({});

  const searchResults = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return [];
    return sections.filter((section) =>
      normalizeSearchText(section).includes(query),
    );
  }, [searchValue]);

  const hasSearch = searchValue.trim().length > 0;

  const highlightText = (text) => {
    if (!hasSearch || typeof text !== "string") return text;
    const query = searchValue.trim();
    if (!query) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={`${part}-${index}`}>{part}</mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ),
    );
  };

  const scrollToSection = useCallback((sectionId) => {
    const element =
      sectionRefs.current[sectionId] || document.getElementById(sectionId);
    if (!element) return;

    const parentSection = sections.find((section) =>
      (section.subpoints || []).some((point) => point.id === sectionId),
    );

    const headerOffset = 24;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: Math.max(0, elementPosition - headerOffset),
      behavior: "smooth",
    });

    setActiveSection(parentSection?.id || sectionId);
    setMobileTocOpen(false);

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${sectionId}`,
    );
  }, []);

  useEffect(() => {
    const sectionRefMap = sectionRefs.current;

    const elements = sections
      .map((section) => sectionRefMap[section.id])
      .filter(Boolean);

    if (!elements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length) setActiveSection(visible[0].target.id);
      },
      {
        root: null,
        rootMargin: "-12% 0px -72% 0px",
        threshold: [0, 0.05, 0.2],
      },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");

    if (!hash) return undefined;

    const timeout = window.setTimeout(() => {
      scrollToSection(hash);
    }, 120);

    return () => window.clearTimeout(timeout);
  }, [scrollToSection]);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCopyLink = async (sectionId) => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${sectionId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${sectionId}`,
      );
    }
    setCopiedSection(sectionId);
    window.setTimeout(() => setCopiedSection(""), 1600);
  };

  const renderSectionLine = (line, index, section) => {
    const trimmed = line.trim();

    if (trimmed.startsWith("•")) {
      return (
        <li key={`${section.id}-line-${index}`}>
          <span className="fdc-terms-list__marker">
            <CheckCircle aria-hidden="true" />
          </span>
          <span>{highlightText(trimmed.replace(/^•\s*/, ""))}</span>
        </li>
      );
    }

    if (/^\d+\.\s/.test(trimmed)) {
      return (
        <li
          key={`${section.id}-line-${index}`}
          className="fdc-terms-numbered-line"
        >
          <span>{highlightText(trimmed)}</span>
        </li>
      );
    }

    if (
      trimmed === "Six Months" ||
      trimmed === "One Month" ||
      trimmed === "20 New Verified Active Clients Per Month" ||
      trimmed === "20 Clients Within the First Three Months" ||
      trimmed === "100 Verified Active Clients (VACs)" ||
      trimmed === "₦100,000" ||
      trimmed === "₦15,000"
    ) {
      return (
        <div className="fdc-terms-emphasis" key={`${section.id}-line-${index}`}>
          {highlightText(trimmed)}
        </div>
      );
    }

    if (trimmed.includes("Prospecting → Marketing → Consultation")) {
      return (
        <div className="fdc-terms-process" key={`${section.id}-line-${index}`}>
          {trimmed.split("→").map((step, stepIndex, arr) => (
            <React.Fragment key={step.trim()}>
              <span>{step.trim()}</span>
              {stepIndex < arr.length - 1 && (
                <ChevronRight aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (/^\d+%$/.test(trimmed)) {
      return (
        <span
          className="fdc-terms-performance-chip"
          key={`${section.id}-line-${index}`}
        >
          {trimmed}
        </span>
      );
    }

    return <p key={`${section.id}-line-${index}`}>{highlightText(trimmed)}</p>;
  };

  const renderSpecialContent = (section) => {
    if (section.id === "performance-linked-salary") {
      return (
        <>
          <div className="fdc-terms-subheading">
            <TrendingUp aria-hidden="true" />
            <div>
              <strong>Salary Performance Bands</strong>
              <span>
                Level 1: 20-VAC monthly target · Full base salary from 70% / 14
                VACs · After initial salary eligibility
              </span>
            </div>
          </div>
          <DataTable
            headers={salaryBands.headers}
            rows={salaryBands.rows}
            compact
          />
          <div className="fdc-terms-callout fdc-terms-callout--info">
            <InfoOutlined aria-hidden="true" />
            <div>
              <strong>Minimum salary rule</strong>
              <p>
                After achieving the initial cumulative 15-VAC salary milestone,
                a Level 1 FDC with zero VACs in a month falls within the ₦15,000
                salary band, subject to the employment agreement and applicable
                law. Commission remains payable on VACs qualifying under Section
                05.
              </p>
            </div>
          </div>
        </>
      );
    }

    if (section.id === "client-acquisition-commission") {
      return (
        <>
          <div className="fdc-terms-subheading">
            <PaymentsOutlined aria-hidden="true" />
            <div>
              <strong>Plan-Based Commission Schedules</strong>
              <span>
                These illustrative base-salary and total-earnings amounts assume
                the FDC has already achieved the one-time cumulative 15-VAC
                salary eligibility milestone. Commission remains separately
                payable on eligible VACs even before that milestone.
              </span>
            </div>
          </div>

          <div className="fdc-comp-plans">
            {["15000", "8000", "5000", "3000"].map((scheduleKey) => (
              <CommissionPlanTable
                key={scheduleKey}
                scheduleKey={scheduleKey}
              />
            ))}
          </div>
        </>
      );
    }

    if (section.id === "rank-progression") {
      return (
        <>
          <div className="fdc-terms-subheading">
            <TrendingUp aria-hidden="true" />
            <div>
              <strong>Illustrative Six-Month Promotion Progression</strong>
              <span>
                Six months is an example, not a minimum waiting period.
              </span>
            </div>
          </div>
          <DataTable
            headers={[
              "Current Level",
              "VACs / Month",
              "Cumulative VAC Milestone",
              "Next Level",
            ]}
            rows={[
              ["Level 1", "20", "120", "Level 2"],
              ["Level 2", "40", "240", "Level 3"],
              ["Level 3", "100", "600", "Level 4"],
              ["Level 4", "200", "1,200", "Level 5"],
              ["Level 5", "300", "1,800", "Level 6"],
              ["Level 6", "400", "2,400", "Level 7"],
            ]}
            compact
          />
        </>
      );
    }

    if (section.id === "fdc-career-levels") {
      return (
        <>
          <DataTable
            title="FDC Career Levels & Promotion Requirements"
            subtitle="Full-target monthly salary, current-level acquisition targets, and the next promotion milestone"
            headers={[
              "FDC Level",
              "Full-Target Monthly Salary",
              "Monthly VAC Target",
              "VACs for Next Promotion",
            ]}
            rows={[
              ["Level 1", "₦100,000", "20", "120"],
              ["Level 2", "₦250,000", "40", "240"],
              ["Level 3", "₦450,000", "100", "600"],
              ["Level 4", "₦700,000", "200", "1,200"],
              ["Level 5", "₦1,000,000", "300", "1,800"],
              ["Level 6", "₦2,300,000", "400", "2,400"],
              ["Level 7", "₦7,000,000", "1,000", "Highest FDC level"],
            ]}
            compact
          />
          <div className="fdc-terms-rank-note">
            <WorkspacePremiumOutlined aria-hidden="true" />
            <span>
              The seven-level career ladder remains part of the employment
              progression framework.
            </span>
          </div>
        </>
      );
    }

    if (section.id === "acceptance") {
      return (
        <div className="fdc-terms-acceptance">
          <div className="fdc-terms-acceptance__intro">
            <strong>Application and Employee Acceptance</strong>
            <p>
              Applicants should complete the Field Development Consultant
              application first. The formal Employee Acceptance form is
              maintained as a separate print-ready document for completion when
              the acceptance stage is reached.
            </p>
          </div>

          <div className="fdc-terms-acceptance__actions">
            <a
              className="fdc-terms-acceptance__button fdc-terms-acceptance__button--primary"
              href="/career/field-development-consultant/application"
            >
              Apply for FDC Position
              <ChevronRight aria-hidden="true" />
            </a>

            <button
              type="button"
              className="fdc-terms-acceptance__button"
              onClick={() => setAcceptanceModalOpen(true)}
            >
              <Print aria-hidden="true" />
              Open Printable Acceptance Form
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <main className="fdc-terms-page">
        <div className="fdc-terms-page__shell">
          <header className="fdc-terms-hero">
            <div className="fdc-terms-hero__top fx fx-ac fx-jb wrap g3">
              <div className="fdc-terms-brand fx fx-ac g2">
                <div className="fdc-terms-brand__icon" aria-hidden="true">
                  <WorkOutline />
                </div>
                <div>
                  <div className="fdc-terms-brand__name fw600">
                    {documentConfig.companyName}
                  </div>
                  <div className="fdc-terms-brand__label">
                    People Operations · Field Development
                  </div>
                </div>
              </div>

              <div className="fdc-terms-status fx fx-ac g1">
                <CheckCircle aria-hidden="true" />
                <span>{documentConfig.status}</span>
              </div>
            </div>

            <div className="fdc-terms-hero__content">
              <div className="fdc-terms-hero__eyebrow fx fx-ac g1">
                <BadgeOutlined aria-hidden="true" />
                <span>EMPLOYMENT TERMS</span>
              </div>

              <h1 className="fdc-terms-hero__title">
                {documentConfig.documentTitle}
              </h1>
              <p className="fdc-terms-hero__subtitle">
                {documentConfig.documentSubtitle}
              </p>

              <p className="fdc-terms-hero__description">
                Employment, field-acquisition, compensation, performance,
                recovery, career-progression and retirement framework for
                Universe Inventory FDC employees.
              </p>

              <div className="fdc-terms-meta">
                <div className="fdc-terms-meta__item">
                  <span>Position</span>
                  <strong>{documentConfig.position}</strong>
                </div>
                <div className="fdc-terms-meta__item">
                  <span>Employment Type</span>
                  <strong>{documentConfig.employmentType}</strong>
                </div>
                <div className="fdc-terms-meta__item">
                  <span>Department</span>
                  <strong>{documentConfig.department}</strong>
                </div>
                <div className="fdc-terms-meta__item">
                  <span>Reporting Structure</span>
                  <strong>{documentConfig.reportingStructure}</strong>
                </div>
              </div>
            </div>
          </header>

          <section className="fdc-terms-review-notice">
            <div className="fdc-terms-review-notice__icon">
              <InfoOutlined aria-hidden="true" />
            </div>
            <div>
              <strong>Employment document</strong>
              <p>
                Issued by {documentConfig.issuer}. This page preserves the
                supplied FDC employment, performance, commission and
                career-progression terms as an operational employment document.
              </p>
            </div>
          </section>

          <section className="fdc-terms-search">
            <div className="fdc-terms-search__box">
              <Search aria-hidden="true" />
              <input
                type="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search FDC employment terms"
                aria-label="Search FDC employment terms"
              />
              {searchValue && (
                <button
                  type="button"
                  className="fdc-terms-search__clear"
                  onClick={() => setSearchValue("")}
                  aria-label="Clear search"
                >
                  <Close aria-hidden="true" />
                </button>
              )}
            </div>
            {hasSearch && (
              <div className="fdc-terms-search__results">
                {searchResults.length}{" "}
                {searchResults.length === 1 ? "result" : "results"} found
              </div>
            )}
          </section>

          <div className="fdc-terms-mobile-toc">
            <button
              type="button"
              className="fdc-terms-mobile-toc__button"
              onClick={() => setMobileTocOpen((current) => !current)}
              aria-expanded={mobileTocOpen}
              aria-controls="fdc-terms-mobile-navigation"
            >
              <span>
                <MenuBook aria-hidden="true" /> Table of Contents
              </span>
              <ExpandMore
                className={
                  mobileTocOpen
                    ? "fdc-terms-mobile-toc__arrow fdc-terms-mobile-toc__arrow--open"
                    : "fdc-terms-mobile-toc__arrow"
                }
              />
            </button>

            {mobileTocOpen && (
              <nav
                id="fdc-terms-mobile-navigation"
                className="fdc-terms-mobile-toc__content"
                aria-label="FDC employment terms sections"
              >
                {sections.map((section) => (
                  <button
                    type="button"
                    key={section.id}
                    className={
                      activeSection === section.id
                        ? "fdc-terms-toc__item fdc-terms-toc__item--active"
                        : "fdc-terms-toc__item"
                    }
                    onClick={() => scrollToSection(section.id)}
                  >
                    <span className="fdc-terms-toc__number">
                      {section.number}
                    </span>
                    <span className="fdc-terms-toc__title">
                      {section.title}
                    </span>
                  </button>
                ))}
              </nav>
            )}
          </div>

          <div className="fdc-terms-layout">
            <aside className="fdc-terms-sidebar">
              <div className="fdc-terms-sidebar__inner">
                <div className="fdc-terms-sidebar__heading">
                  <MenuBook aria-hidden="true" />
                  <div>
                    <strong>Table of Contents</strong>
                    <span>{sections.length} employment sections</span>
                  </div>
                </div>

                <nav
                  className="fdc-terms-toc"
                  aria-label="FDC employment terms sections"
                >
                  {sections.map((section) => (
                    <button
                      type="button"
                      key={section.id}
                      data-section-id={section.id}
                      className={
                        activeSection === section.id
                          ? "fdc-terms-toc__item fdc-terms-toc__item--active"
                          : "fdc-terms-toc__item"
                      }
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="fdc-terms-toc__number">
                        {section.number}
                      </span>
                      <span className="fdc-terms-toc__title">
                        {section.title}
                      </span>
                      {activeSection === section.id && (
                        <ChevronRight
                          className="fdc-terms-toc__chevron"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <article className="fdc-terms-document">
              {sections.map((section) => {
                const bulletLines = section.lines.filter((line) =>
                  line.trim().startsWith("•"),
                );
                const normalLines = section.lines.filter(
                  (line) => !line.trim().startsWith("•"),
                );

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    ref={(element) => {
                      sectionRefs.current[section.id] = element;
                    }}
                    className={
                      hasSearch && searchResults.includes(section)
                        ? "fdc-terms-section fdc-terms-section--search-match"
                        : "fdc-terms-section"
                    }
                  >
                    <div className="fdc-terms-section__header">
                      <div className="fdc-terms-section__number">
                        § {section.number}
                      </div>
                      <div className="fdc-terms-section__heading-wrap">
                        <h2>{highlightText(section.title)}</h2>
                        <button
                          type="button"
                          className="fdc-terms-section__link"
                          onClick={() => handleCopyLink(section.id)}
                          aria-label={`Copy link to ${section.title}`}
                        >
                          <Link aria-hidden="true" />
                          <span>
                            {copiedSection === section.id
                              ? "Link copied"
                              : "Copy link"}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="fdc-terms-section__body">
                      {normalLines.map((line, index) =>
                        renderSectionLine(line, index, section),
                      )}

                      {section.subpoints?.length > 0 && (
                        <ol className="fdc-terms-subpoints">
                          {section.subpoints.map((point) => (
                            <li
                              key={point.id}
                              id={point.id}
                              className="fdc-terms-numbered-line"
                            >
                              {highlightText(`${point.number}. ${point.title}`)}
                            </li>
                          ))}
                        </ol>
                      )}

                      {section.afterSubpoints?.map((line, index) =>
                        renderSectionLine(line, index, section),
                      )}

                      {bulletLines.length > 0 && (
                        <ul className="fdc-terms-list">
                          {bulletLines.map((line, index) =>
                            renderSectionLine(line, index, section),
                          )}
                        </ul>
                      )}

                      {renderSpecialContent(section)}
                    </div>
                  </section>
                );
              })}

              <section className="fdc-terms-final-card">
                <div className="fdc-terms-final-card__icon">
                  <WorkOutline aria-hidden="true" />
                </div>
                <div>
                  <h2>Field Development Consultant Employment Terms</h2>
                  <p>
                    {documentConfig.companyName} · Version{" "}
                    {documentConfig.version}
                  </p>
                  <p>
                    This document forms the operating employment, performance,
                    commission and career-progression framework for the FDC role
                    together with the employee's offer letter and applicable
                    company policies.
                  </p>
                </div>
              </section>
            </article>
          </div>
        </div>

        <div className="fdc-terms-floating-actions">
          {showBackToTop && (
            <button
              type="button"
              className="fdc-terms-floating-button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
            >
              <ArrowUpward aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="fdc-terms-floating-button fdc-terms-floating-button--print"
            onClick={() => window.print()}
            aria-label="Print FDC employment terms"
          >
            <Print aria-hidden="true" />
          </button>
        </div>
      </main>

      {acceptanceModalOpen && (
        <FDCEmploymentAcceptanceModal
          onClose={() => setAcceptanceModalOpen(false)}
        />
      )}
    </>
  );
}

export default FDCEmploymentTerms;
